import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Download, Pause, Play } from "lucide-react";
import PropTypes from "prop-types";
import { toast } from "sonner";

import { cn } from "@/lib/utils";
import {
  PLAYBACK_BLOCKED_MESSAGE,
  PLAYBACK_FAILED_MESSAGE,
} from "constants/AudioPlayback";

const DURATION_PLACEHOLDER = "--:--";

const formatDuration = seconds => {
  const total = Math.floor(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const rest = String(total % 60).padStart(2, "0");
  return hours
    ? `${hours}:${String(minutes).padStart(2, "0")}:${rest}`
    : `${minutes}:${rest}`;
};

/**
 * Compact audio player: a play/pause button ringed by a circular progress bar,
 * with an optional duration readout and download affordance. Use for list-item
 * previews (e.g. voicemail rows). For mute / speed / scrubbing, reach for
 * AudioElement.
 */
export function RoundedAudioPlayer({
  src,
  id,
  size = 32,
  stroke = 2,
  download = false,
  showDuration = false,
  className,
}) {
  const audioRef = useRef(null);
  const playRequestedRef = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(null);
  const [renderedSrc, setRenderedSrc] = useState(src);
  const toastId = useId();

  // A recycled table row swaps src on an already-mounted player, so the
  // previous recording's progress and duration have to be dropped.
  if (src !== renderedSrc) {
    setRenderedSrc(src);
    setPlaying(false);
    setProgress(0);
    setDuration(null);
  }

  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - progress);
  const innerSize = size - stroke * 3;

  const failPlayback = useCallback(
    (message = PLAYBACK_FAILED_MESSAGE) => {
      // A fatal mid-stream error halts the element without clearing `paused`
      // the way end-of-playback does, so pause explicitly or the next click
      // reads as a pause rather than a retry.
      audioRef.current.pause();
      setPlaying(false);
      setProgress(0);
      playRequestedRef.current = false;
      // A shared id replaces the standing toast instead of stacking a new one
      // when the same recording is retried.
      toast.error(message, { id: toastId });
    },
    [toastId]
  );

  const toggle = () => {
    const el = audioRef.current;
    if (!el.paused) {
      el.pause();
      setPlaying(false);
      return;
    }
    playRequestedRef.current = true;
    setPlaying(true);
    el.play().catch(err => {
      // play() rejects with AbortError when a pause or a src swap interrupts
      // the pending request. That isn't a broken recording, so stay quiet.
      if (err?.name === "AbortError") return;
      failPlayback(
        err?.name === "NotAllowedError"
          ? PLAYBACK_BLOCKED_MESSAGE
          : PLAYBACK_FAILED_MESSAGE
      );
    });
  };

  useEffect(() => {
    const el = audioRef.current;
    playRequestedRef.current = false;

    const onLoadedMetadata = () =>
      setDuration(Number.isFinite(el.duration) ? el.duration : null);
    const onTime = () =>
      setProgress(el.duration ? el.currentTime / el.duration : 0);
    const onEnd = () => {
      setPlaying(false);
      setProgress(0);
    };
    // Media errors also fire while preloading metadata, long before anyone
    // clicks play. Staying quiet until playback is requested keeps a table of
    // dead attachment URLs from firing one toast per row on render.
    const onError = () => {
      if (playRequestedRef.current) failPlayback();
    };

    el.addEventListener("loadedmetadata", onLoadedMetadata);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("ended", onEnd);
    el.addEventListener("error", onError);

    // A cached source can reach HAVE_METADATA before the listener is attached,
    // in which case loadedmetadata has already fired and won't fire again.
    if (el.readyState >= HTMLMediaElement.HAVE_METADATA) onLoadedMetadata();

    return () => {
      el.removeEventListener("loadedmetadata", onLoadedMetadata);
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("ended", onEnd);
      el.removeEventListener("error", onError);
    };
  }, [src, failPlayback]);

  return (
    <div className={cn("tw:inline-flex tw:items-center tw:gap-1", className)}>
      <div className="tw:relative" style={{ width: size, height: size }}>
        <audio id={id} ref={audioRef} src={src} preload="metadata" />
        <svg
          className="tw:absolute tw:inset-0 tw:-rotate-90"
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          aria-hidden="true"
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            strokeWidth={stroke}
            className="tw:stroke-muted"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className="tw:stroke-primary tw:transition-[stroke-dashoffset] tw:duration-100 tw:ease-linear"
          />
        </svg>
        <button
          type="button"
          onClick={toggle}
          aria-label={playing ? "Pause" : "Play"}
          className="tw:absolute tw:top-1/2 tw:left-1/2 tw:flex tw:-translate-x-1/2 tw:-translate-y-1/2 tw:items-center tw:justify-center tw:rounded-full tw:bg-primary tw:text-primary-foreground tw:transition-colors tw:hover:bg-primary/80 tw:focus-visible:ring-2 tw:focus-visible:ring-ring/50 tw:focus-visible:outline-none"
          style={{ width: innerSize, height: innerSize }}
        >
          {playing ? (
            <Pause className="tw:size-1/2" />
          ) : (
            <Play className="tw:size-1/2 tw:translate-x-px" />
          )}
        </button>
      </div>
      {showDuration && (
        // The slot keeps its width across the placeholder-to-duration swap so
        // the row doesn't reflow when metadata lands.
        <span className="tw:min-w-9 tw:text-xs tw:tabular-nums tw:text-muted-foreground">
          {duration === null ? DURATION_PLACEHOLDER : formatDuration(duration)}
        </span>
      )}
      {download && (
        <a
          href={src}
          download
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Download"
          className="tw:flex tw:items-center tw:justify-center tw:rounded-full tw:text-muted-foreground! tw:transition-colors tw:hover:bg-muted tw:hover:text-foreground! tw:focus-visible:ring-2 tw:focus-visible:ring-ring/50 tw:focus-visible:outline-none"
          style={{
            height: size,
            width: size,
          }}
        >
          <Download className="tw:size-1/2" />
        </a>
      )}
    </div>
  );
}

RoundedAudioPlayer.propTypes = {
  src: PropTypes.string.isRequired,
  id: PropTypes.string,
  size: PropTypes.number,
  stroke: PropTypes.number,
  download: PropTypes.bool,
  showDuration: PropTypes.bool,
  className: PropTypes.string,
};
